import { ProductGroupReference } from './ProductGroupStore';
import { DateTime } from '../DateTime';
import { TeamMemberReference } from '../TeamMemberStore';
import {
  types,
  SnapshotIn,
  SnapshotOut,
  Instance,
  IAnyModelType,
  getEnv,
  getRoot,
  flow,
} from 'mobx-state-tree';
import { modelWithCRUD } from '../utils/modelWithCRUD';
import { IRootEnv, RootModelType } from '../Root';
import { CompanyReference, ICompany } from '../contacts/CompanyStore';
import { CommomMixin } from '../Mixins';

export enum BarcodeType {
  EAN_13 = 'EAN-13',
}

export interface IBarcode {
  value: string;
  type?: BarcodeType;
  description?: string;
}

export const Barcode = types
  .model('Barcode', {
    type: types.maybe(
      types.enumeration<BarcodeType>('BarcodeType', Object.values(BarcodeType))
    ),
    value: types.string,
  })
  .views((self) => ({
    get description(): string {
      if (self.type) return `${self.type} ${self.value}`;
      return self.value;
    },
  }));

export const CostPrice = types.model({
  precise: types.maybeNull(types.number),
  precise_currency: types.maybeNull(types.string),
  minimum: types.maybeNull(types.number),
  minimum_currency: types.maybeNull(types.string),
  maximum: types.maybeNull(types.number),
  maximum_currency: types.maybeNull(types.string),
});

export interface ICostPrice extends Instance<typeof CostPrice> {}

export const BOMItem = types.model('BOMItem', {
  id: types.union(types.identifier, types.identifierNumber),
  quantity: types.number,
  // TODO: Maybe we should use an intermediate relation to hold the default supplier;
  // that way we can automatically remove the default supplier when removing the supplier from the child product.
  default_supplier_item: types.maybeNull(
    types.late(() => SupplierItemReference)
  ),
  product: types.late(() => ProductReference),
  parent: types.late(() => ProductReference),
  cost_price: CostPrice,
});

export const BOMItemStore = modelWithCRUD('bill-of-materials', BOMItem).named(
  'BOMItemStore'
);

export interface IBOMItem extends Instance<typeof BOMItem> {}
export interface IBOMItemSnapshotIn extends SnapshotIn<typeof BOMItem> {}
export interface IBOMItemSnapshotOut extends SnapshotOut<typeof BOMItem> {}

export const BOMItemReference = types.reference(BOMItem, {
  get(identifier: string, parent): any | null {
    return (getRoot(parent!) as RootModelType).bomItemStore.getItemNaive(
      identifier
    );
  },
  set(value) {
    return value.id;
  },
});

export const SupplierItem = types
  .model('SupplierProductItem', {
    id: types.union(types.identifier, types.identifierNumber),
    deleted_at: types.maybeNull(DateTime),
    supplier: types.late(() => CompanyReference),
    supplier_number: types.maybe(types.string),
    stock: types.number,
    cost_price: types.number,
    cost_price_currency: types.optional(types.string, 'DKK'),
    product: types.late((): typeof ProductReference => ProductReference),
  })
  .views((self) => ({
    get env() {
      return getEnv<IRootEnv>(self);
    },
  }));

export interface ISupplierItem extends Instance<typeof SupplierItem> {}
export interface ISupplierItemSnapshotIn
  extends SnapshotIn<typeof SupplierItem> {}
export interface ISupplierItemSnapshotOut
  extends SnapshotOut<typeof SupplierItem> {}

export const SupplierItemStore = modelWithCRUD(
  'supplier-items',
  SupplierItem
).named('SupplierProductItemStore');

export const SupplierItemReference = types.reference(SupplierItem, {
  get(identifier: string, parent): any {
    return (getRoot(parent!) as RootModelType).supplierItemStore.getItemNaive(
      identifier
    );
  },
  set(value) {
    return value.id;
  },
});

export enum Unit {
  MILLIMETERS = 'millimeter',
  CENTIMETERS = 'centimeter',
  METERS = 'meter',
  KILOMETER = 'kilometer',

  MILLILITER = 'milliliter',
  LITER = 'liter',

  SECOND = 'second',
  MINUTE = 'minute',
  HOUR = 'hour',
  DAY = 'day',
  WEEK = 'week',
  MONTH = 'month',
  YEAR = 'year',

  PCS = 'pcs',
}

export enum ProductType {
  GOOD = 'good',
  SERVICE = 'service',
  PACKAGE = 'package',
}

export const Product = types
  .compose(
    CommomMixin,
    types.model({
      id: types.union(types.identifier, types.identifierNumber),
      type: types.enumeration<ProductType>(
        'ProductType',
        Object.values(ProductType)
      ),

      deleted_at: types.maybeNull(DateTime),

      name: types.string,
      sku: types.maybeNull(types.string),
      tariff_number: types.maybeNull(types.string),
      unit: types.enumeration<Unit>(Object.values(Unit)),
      manufacturer_number: types.maybeNull(types.string),
      barcode: types.maybeNull(types.late((): IAnyModelType => Barcode)),
      retail_price: types.number,
      retail_price_currency: types.optional(types.string, 'DKK'),

      group: types.maybeNull(types.late(() => ProductGroupReference)),

      own_cost_price: types.maybeNull(types.number),
      own_cost_price_currency: types.maybeNull(types.string),

      stock: types.maybeNull(types.number),
      available_stock: types.maybeNull(types.number),
      reserved_stock: types.maybeNull(types.number),
      incoming_stock: types.maybeNull(types.number),

      shadow_stock: types.maybeNull(types.number),

      cost_price: CostPrice,
      contribution_margin: CostPrice,
      contribution_margin_ratio: CostPrice,

      task_managers: types.array(types.late(() => TeamMemberReference)),

      bill_of_materials: types.array(types.late(() => BOMItemReference)),

      supplier_items: types.array(types.late(() => SupplierItemReference)),
      // The default supplier is the supplier product item to use as default - not the actual supplier.
      default_supplier_item: types.maybeNull(
        types.late(() => SupplierItemReference)
      ),
    })
  )
  .named('Product')
  .actions((self) => ({
    setDefaultSupplierProductItem: flow(function* update(item: any) {
      // We must use any type; otherwise we create a recursive circular type
      yield (getRoot(self) as any).productStore.updateItem(self as any, {
        default_supplier_item: item.id,
      });
    }),
  }));

export const ProductReference = types.reference(
  types.late((): IAnyModelType => Product),
  {
    get(identifier: string, parent): any {
      return (getRoot(parent!) as RootModelType).productStore.getItemNaive(
        identifier
      );
    },
    set(value) {
      return value.id;
    },
  }
);

export interface IProductStockLocation {
  stock: number;
  incoming: number;
  reserved: number;
  available: number;
  location: string;
  location_code: string;
}

export const ProductStore = modelWithCRUD('products', Product)
  .named('ProductStore')
  .actions((self) => ({
    updateStock: flow(function* updateStock(
      product: IProduct,
      quantity: number,
      locationId: string | number,
      note?: string
    ) {
      yield self.env.api.post(`${self.apiPath}/${product.id}/update-stock/`, {
        quantity,
        location: locationId,
        note,
      });
      yield self.getItem(product.id, true);
    }),
    moveStock: flow(function* moveStock(
      product: IProduct,
      values: {
        from_location: string | number;
        to_location: string | number;
        quantity: number;
        note?: string;
      }
    ) {
      yield self.env.api.post(
        `${self.apiPath}/${product.id}/move-stock/`,
        values
      );
      yield self.getItem(product.id, true);
    }),
    previewImport: flow(function* previewImport(
      items: any,
      currency: string,
      supplier?: ICompany
    ) {
      const { data } = yield self.env.api.post(
        `${self.apiPath}/preview-import/`,
        {
          items,
          currency,
          supplier: supplier ? supplier.id : undefined,
        }
      );
      return data;
    }),
    performImport: flow(function* performImport(importData: any) {
      const { data } = yield self.env.api.post<{
        products_created: number[];
        products_updated: number[];
        products_archived: number[];
      }>(`${self.apiPath}/import/`, importData);
      const root = getRoot(self) as RootModelType;
      root.supplierItemStore.invalidateCaches();
      self.invalidateCaches();
      return data;
    }),
    duplicate: flow(function* duplicate(product: IProduct) {
      const data = yield self.env.api
        .post(`${self.apiPath}/${product.id}/duplicate/`, {})
        .then((res) => res.data);
      self.itemMap.put(data);
      self.invalidateCaches();
      return self.getItem(data.id)!;
    }),
    getProductLocations: flow(function* getProductLocations(product: IProduct) {
      const res = yield self.env.api.get(
        `${self.apiPath}/${product.id}/stock-locations/`
      );
      return res.data as { stock_locations: IProductStockLocation[] };
    }),
  }));

export interface IProduct extends Instance<typeof Product> {}
export interface IProductSnapshotIn extends SnapshotIn<typeof Product> {}
export interface IProductSnapshotOut extends SnapshotOut<typeof Product> {}
